// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Search view lifecycle + UI wiring.
 *
 * - Uses the shared SongIndexClient to search the app-level song index.
 * - Persists search toggle preferences in localStorage.
 */

import type { InitialState } from "../../shared/src/types";
import { getEnabledBooks } from "../../shared/src/songbook";
import type { SearchOpts, SongIndexStats } from "../../shared/src/song-index";
import type { GroupedSongResult } from "../../shared/src/song-results";
import type { TranslationInterpolationValue, Translator } from "@unilarva/translator";
import type { TranslatorDomBinding } from "@unilarva/translator/dom";
import { isElementOutOfViewport, isLikelyMobile, prefersReducedMotion } from "./device";
import type { SongIndexClient } from "./song-index-client";
import { initializeGroupedSongResult, renderGroupedSongResult } from "./song-result-renderer";
import {
  SEARCH_PARAM_KEYS,
  parseSearchUrlState,
  serializeViewUrl,
} from "../../shared/src/view-url";

type EntityToggles = {
  titles: boolean;
  lyrics: boolean;
  translations: boolean;
  chapters: boolean;
  authors: boolean;
  offered_to: boolean;
  extra_info: boolean;
  tags: boolean;
  numbers: boolean;
  ids: boolean;
};

type SearchPrefs = {
  entities: EntityToggles;
  variants: Record<string, boolean>;
  sources: Record<string, boolean>;
};

const LS_KEY_PREFS = "sb_search_prefs:v1";

export class SearchManager {
  private readonly state: InitialState;
  private readonly t: Translator;
  private readonly translationBinding: TranslatorDomBinding;
  private readonly songIndex: SongIndexClient;

  private indexReady = false;
  private workerKnownVariants: string[] = [];
  private workerKnownSources: string[] = [];

  // DOM refs (search pane)
  private rootEl: HTMLElement | null = null;
  private inputEl: HTMLInputElement | null = null;
  private clearBtnEl: HTMLButtonElement | null = null;
  private loadingEl: HTMLElement | null = null;
  private loadingTextEl: HTMLElement | null = null;
  private errorEl: HTMLElement | null = null;
  private errorTextEl: HTMLElement | null = null;
  private resultsEl: HTMLElement | null = null;
  private filtersWrapEl: HTMLElement | null = null;
  private filtersToggleBtnEl: HTMLButtonElement | null = null;
  private filtersSummaryEl: HTMLElement | null = null;
  private variantsEl: HTMLElement | null = null;
  private sourcesEl: HTMLElement | null = null;
  private entitiesEl: HTMLElement | null = null;

  // UI state
  private prefs: SearchPrefs;
  private persistedPrefs: SearchPrefs;
  private query = "";
  private queryNorm = "";

  private loadingState: "idle" | "indexing" | "searching" | "message" = "idle";
  private loadingMessage: string | null = null;
  private loadingMessageI18nKey: string | null = null;
  private loadingMessageValues: Readonly<Record<string, TranslationInterpolationValue>> | undefined;
  private awaitingFirstResults = false;
  private forceNextSearch = false;
  private filtersCollapsed = false;
  private collapseFiltersOnNextResults = true;

  private searchDebounceTimer: number | null = null;
  private nextRequestId = 1;
  private latestSearchRequestId = 0;
  private lastAppliedSearchUrlSignature: string | null = null;

  // Focus handoff flags
  private focusRelocateFrame: number | null = null;
  private focusRelocateListenersAttached = false;
  private focusRelocateLastOrigin: string | null = null;

  private coverPreviewEl: HTMLElement | null = null;
  private coverPreviewSource: HTMLElement | null = null;
  private keyboardResult: HTMLElement | null = null;
  private returningToSearchInput = false;

  private active = false;
  private preservedSsrSignature: string | null = null;

  constructor(
    state: InitialState,
    translator: Translator,
    translationBinding: TranslatorDomBinding,
    songIndex: SongIndexClient,
  ) {
    this.state = state;
    this.t = translator;
    this.translationBinding = translationBinding;
    this.songIndex = songIndex;

    this.songIndex.onProgress((loaded, total) => this.handleIndexProgress(loaded, total));
    this.songIndex.onInitDone(stats => this.handleIndexReady(stats));

    const initialPrefs = this.loadPrefs();
    this.prefs = initialPrefs;
    this.persistedPrefs = this.clonePrefs(initialPrefs);
    document.addEventListener("click", this.handleResultCoverClick);
  }

  private clonePrefs(p: SearchPrefs): SearchPrefs {
    return {
      entities: { ...p.entities },
      variants: { ...p.variants },
      sources: { ...p.sources },
    };
  }

  private searchUrlSignature(): string {
    const params = new URL(window.location.href).searchParams;
    return JSON.stringify(SEARCH_PARAM_KEYS.map(key => params.get(key)));
  }

  private hasSearchParamsInUrl(): boolean {
    const params = new URL(window.location.href).searchParams;
    return SEARCH_PARAM_KEYS.some(name => params.has(name));
  }

  private resetActivePrefsFromStorage(): void {
    this.prefs = this.clonePrefs(this.persistedPrefs);
    this.mergePrefsWithKnownVariants();
    this.ensureDefaultsForGroup(this.workerKnownVariants, this.prefs.variants);
    this.ensureDefaultsForGroup(this.workerKnownSources, this.prefs.sources);
  }

  /** Applies public URL state without changing the saved local preferences. */
  private applySearchParamsFromUrl(): void {
    const state = parseSearchUrlState(new URL(window.location.href).searchParams);
    this.query = state.query;
    if (this.inputEl) this.inputEl.value = this.query;
    const publicState = this.hasSearchParamsInUrl();
    const applyGroup = (
      ids: string[] | "all" | null,
      target: Record<string, boolean>,
      known: string[],
    ): void => {
      if (!publicState && ids === null) return;
      const all = ids === null || ids === "all";
      known.forEach(id => {
        target[id] = all || ids.includes(id);
      });
    };
    applyGroup(state.books, this.prefs.sources, this.workerKnownSources);
    applyGroup(state.variants, this.prefs.variants, this.workerKnownVariants);
    const ents = state.entities;
    if (publicState || ents !== null) {
      const allKeys = Object.keys(this.prefs.entities) as (keyof EntityToggles)[];
      if (ents === null || ents === "all") {
        allKeys.forEach(k => {
          this.prefs.entities[k] = true;
        });
      } else {
        allKeys.forEach(k => {
          this.prefs.entities[k] = false;
        });
        ents.forEach(k => {
          const key = k as keyof EntityToggles;
          if (key in this.prefs.entities) this.prefs.entities[key] = true;
        });
      }
    }

    // Do not save to localStorage here. UI will reflect current prefs.
    // Refresh UI to reflect parsed prefs and query.
    this.applyPrefsToEntityToggles();
    this.applyPrefsToVariantToggles();
    this.applyPrefsToSourceToggles();
    this.updateEntityToggleVisibility();
    this.updateVariantToggleVisibility();
    this.updateSourceToggleVisibility();

    this.filtersToggleBtnEl?.setAttribute("aria-expanded", "true");
  }

  /** Writes the complete effective search state without adding a history entry. */
  private updateUrlFromState(): void {
    if (!this.active) return;
    this.preservedSsrSignature = null;
    const enabledFromMap = (map: Record<string, boolean>, known: string[]): string[] => {
      if (!known.length) return [];
      return known.filter(id => map[id] !== false);
    };

    const sourcesEnabled = enabledFromMap(this.prefs.sources, this.workerKnownSources);
    const variantsEnabled = enabledFromMap(this.prefs.variants, this.workerKnownVariants);

    const entityEnabled = Object.entries(this.prefs.entities)
      .filter(([, v]) => v)
      .map(([k]) => k);

    const next = serializeViewUrl(
      {
        view: "search",
        search: {
          query: this.query,
          books: sourcesEnabled,
          variants: variantsEnabled,
          entities: entityEnabled,
        },
      },
      { defaultLanguage: this.state.site.defaultLanguage ?? "en" },
    );
    window.history.replaceState(window.history.state, "", next);
    this.lastAppliedSearchUrlSignature = this.searchUrlSignature();
  }

  /** Save only the group that was explicitly changed. */
  private savePrefsScoped(group: "entities" | "variants" | "sources"): void {
    if (group === "entities") {
      this.persistedPrefs.entities = { ...this.prefs.entities };
    } else if (group === "variants") {
      this.persistedPrefs.variants = { ...this.prefs.variants };
    } else {
      this.persistedPrefs.sources = { ...this.prefs.sources };
    }
    this.savePrefs();
  }

  handleViewChange(previousView: string, nextView: string): void {
    if (previousView !== nextView) this.closeCoverPreview(false);
    this.active = nextView === "search";
    if (nextView !== "search") {
      this.latestSearchRequestId = this.nextRequestId++;
      if (this.searchDebounceTimer !== null) window.clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
      document.removeEventListener("keydown", this.handleSearchShortcut);
      this.keyboardResult = null;
      this.returningToSearchInput = false;
    }
    if (nextView !== "search") {
      return;
    }

    // View activation: wire DOM + initialize worker/index.
    this.ensureDom();
    this.ensureWired();
    document.addEventListener("keydown", this.handleSearchShortcut);
    const searchUrlSignature = this.searchUrlSignature();
    if (previousView === "search" && searchUrlSignature === this.lastAppliedSearchUrlSignature) {
      return;
    }

    this.seedKnownVariantsAndSources(getEnabledBooks(this.state.books));
    this.resetActivePrefsFromStorage();
    this.applySearchParamsFromUrl();
    this.lastAppliedSearchUrlSignature = searchUrlSignature;
    if (this.rootEl?.dataset.sbSearchSsr === "1") {
      delete this.rootEl.dataset.sbSearchSsr;
      this.preservedSsrSignature = searchUrlSignature;
      this.resultsEl?.querySelectorAll<HTMLElement>(".sb-search-hit").forEach(article => {
        initializeGroupedSongResult(article, { translator: this.t, books: this.state.books });
      });
      if (this.query.trim()) {
        this.setResultsStatus(
          this.resultsEl?.querySelectorAll(".sb-search-hit").length ?? 0,
          this.resultsEl?.querySelectorAll(".sb-search-appearance").length ?? 0,
          this.rootEl.dataset.sbSearchTruncated === "1",
        );
      } else this.setLoadingState("idle");
    } else if (this.preservedSsrSignature !== searchUrlSignature) {
      this.preservedSsrSignature = null;
    }
    this.ensureWorkerStarted();
    if (this.preservedSsrSignature === searchUrlSignature) return;

    // Focus the search input when entering the search view.
    if (previousView !== nextView) {
      try {
        this.inputEl?.focus({ preventScroll: true });
      } catch {
        this.inputEl?.focus();
      }
    }

    // Always re-run search on activation so results are correct after returning.
    this.collapseFiltersOnNextResults = true;
    this.triggerSearchSoon(0);
  }

  // ── Search keyboard navigation ────────────────────────────────────────

  /** Handles search-only find and result navigation without scrolling the outer shell. */
  private readonly handleSearchShortcut = (event: KeyboardEvent): void => {
    if (event.defaultPrevented || event.isComposing || event.altKey || this.coverPreviewEl) return;
    const find =
      (event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === "f";
    const result = event.key === "F3" && !event.ctrlKey && !event.metaKey;
    if (!find && !result) return;
    const pane = this.rootEl?.closest<HTMLElement>('[data-sb-pane="search"]');
    if (!pane) return;
    event.preventDefault();
    const behavior: ScrollBehavior = prefersReducedMotion() ? "instant" : "smooth";
    if (find) {
      this.keyboardResult = null;
      this.returningToSearchInput = pane.scrollTop > 1;
      pane.scrollTo({ top: 0, behavior });
      this.inputEl?.focus({ preventScroll: true });
      return;
    }
    const cards = Array.from(
      this.resultsEl?.querySelectorAll<HTMLElement>("[data-sb-search-hit]") ?? [],
    );
    if (!cards.length) return;
    this.returningToSearchInput = false;
    const focused = document.activeElement?.closest<HTMLElement>("[data-sb-search-hit]");
    let index = cards.findIndex(card => card === (focused ?? this.keyboardResult));
    if (index >= 0) {
      index = (index + (event.shiftKey ? -1 : 1) + cards.length) % cards.length;
    } else {
      const top = pane.getBoundingClientRect().top + pane.clientTop;
      index = event.shiftKey
        ? cards.indexOf(
            [...cards].reverse().find(card => card.getBoundingClientRect().top < top - 1)!,
          )
        : cards.findIndex(card => card.getBoundingClientRect().top > top + 1);
      if (index < 0) index = event.shiftKey ? cards.length - 1 : 0;
    }
    const card = cards[index];
    this.keyboardResult = card;
    card.tabIndex = -1;
    card.focus({ preventScroll: true });
    pane.scrollTo({
      top:
        pane.scrollTop +
        card.getBoundingClientRect().top -
        pane.getBoundingClientRect().top -
        pane.clientTop,
      behavior,
    });
  };

  // ── Song index lifecycle ──────────────────────────────────────────────

  private ensureWorkerStarted(): void {
    if (!this.indexReady && this.preservedSsrSignature === null) this.setLoadingState("indexing");
    void this.songIndex.init().catch(e => {
      if (this.active && this.preservedSsrSignature === null)
        this.showTranslatedError("search.unavailable-offline", e);
    });
  }

  private translatedStatusText(key: string, error?: unknown): string {
    const translated = this.t.translateKey(key).trim();
    if (translated && translated !== key) return translated;
    if (error != null) return error instanceof Error ? error.message : String(error);
    return "";
  }

  private seedKnownVariantsAndSources(books: ReturnType<typeof getEnabledBooks>): void {
    const variantSet = new Set<string>();
    const sourceSet = new Set<string>();
    books.forEach(b => {
      const v = (b as any).variant || "default";
      if (v) variantSet.add(String(v).trim());
      const s = (b as any).source_book_id || "";
      if (s) sourceSet.add(String(s).trim());
    });
    this.workerKnownVariants = Array.from(variantSet).sort((a, b) => a.localeCompare(b));
    this.workerKnownSources = Array.from(sourceSet).sort((a, b) => a.localeCompare(b));
    this.mergePrefsWithKnownVariants();
    this.ensureDefaultsForGroup(this.workerKnownVariants, this.prefs.variants);
    this.ensureDefaultsForGroup(this.workerKnownSources, this.prefs.sources);
    this.applyPrefsToVariantToggles();
    this.applyPrefsToSourceToggles();
    this.updateVariantToggleVisibility();
    this.updateSourceToggleVisibility();
  }

  private ensureDefaultsForGroup(names: string[], prefsMap: Record<string, boolean>): void {
    if (!names.length) return;
    const allOff = names.every(n => prefsMap[n] === false || prefsMap[n] === undefined);
    if (allOff) {
      names.forEach(n => {
        prefsMap[n] = true;
      });
    }
  }

  private handleIndexProgress(loaded: number, total: number): void {
    if (!this.active || this.preservedSsrSignature !== null) return;
    this.setLoadingState("indexing", undefined, "search.loading.progress", { loaded, total });
  }

  private handleIndexReady(stats: SongIndexStats): void {
    this.indexReady = true;
    this.workerKnownVariants = stats.variants;
    this.workerKnownSources = stats.sources;

    this.mergePrefsWithKnownVariants();
    if (this.active && this.hasSearchParamsInUrl()) {
      this.applySearchParamsFromUrl();
      this.lastAppliedSearchUrlSignature = this.searchUrlSignature();
    }
    this.applyPrefsToVariantToggles();
    this.applyPrefsToSourceToggles();
    this.updateVariantToggleVisibility();
    this.updateSourceToggleVisibility();
    if (!this.active || this.preservedSsrSignature === this.searchUrlSignature()) return;

    this.awaitingFirstResults = !!this.queryNorm;
    this.hideError();
    this.setLoadingState(this.awaitingFirstResults ? "indexing" : "idle");
    this.collapseFiltersOnNextResults = true;
    this.triggerSearchSoon(0);
  }

  // ── DOM wiring ───────────────────────────────────────────────────────

  private ensureDom(): void {
    if (this.rootEl && this.rootEl.isConnected) return;

    const pane = document.querySelector<HTMLElement>('[data-sb-pane="search"]');
    const root = pane?.querySelector<HTMLElement>("[data-sb-search-root]") ?? null;
    this.rootEl = root;

    this.inputEl = root?.querySelector<HTMLInputElement>("[data-sb-search-input]") ?? null;
    this.clearBtnEl = root?.querySelector<HTMLButtonElement>("[data-sb-search-clear]") ?? null;
    this.loadingEl = root?.querySelector<HTMLElement>("[data-sb-search-status]") ?? null;
    this.loadingTextEl = root?.querySelector<HTMLElement>("[data-sb-search-status-text]") ?? null;
    this.errorEl = root?.querySelector<HTMLElement>("[data-sb-search-error]") ?? null;
    this.errorTextEl = root?.querySelector<HTMLElement>("[data-sb-search-error-text]") ?? null;
    this.resultsEl = root?.querySelector<HTMLElement>("[data-sb-search-results]") ?? null;
    this.variantsEl = root?.querySelector<HTMLElement>("[data-sb-search-variants]") ?? null;
    this.sourcesEl = root?.querySelector<HTMLElement>("[data-sb-search-sources]") ?? null;
    this.entitiesEl = root?.querySelector<HTMLElement>("[data-sb-search-entities]") ?? null;
    this.filtersToggleBtnEl =
      root?.querySelector<HTMLButtonElement>("[data-sb-search-filters-toggle]") ?? null;
    this.filtersSummaryEl =
      root?.querySelector<HTMLElement>("[data-sb-search-filters-summary]") ?? null;
    this.filtersWrapEl = root?.querySelector<HTMLElement>("[data-sb-search-filters]") ?? null;

    // Restore input value if still in memory.
    if (this.inputEl) this.inputEl.value = this.query;
  }

  private ensureWired(): void {
    if (!this.rootEl) return;
    if (this.rootEl.dataset.sbSearchWired === "1") return;
    this.rootEl.dataset.sbSearchWired = "1";
    this.rootEl.querySelector("[data-sb-search-form]")?.addEventListener("submit", event => {
      event.preventDefault();
      this.query = this.inputEl?.value ?? "";
      this.forceNextSearch = true;
      this.updateUrlFromState();
      this.triggerSearchSoon(0);
    });

    this.ensureFocusRelocateListeners();
    const pane = this.rootEl.closest<HTMLElement>('[data-sb-pane="search"]');
    const resetKeyboardNavigation = () => {
      if (document.activeElement === this.keyboardResult) this.keyboardResult?.blur();
      this.keyboardResult = null;
      this.returningToSearchInput = false;
    };
    for (const type of ["wheel", "touchstart", "pointerdown"]) {
      pane?.addEventListener(type, resetKeyboardNavigation, { passive: true });
    }

    // Enable master toggles that SSR left disabled during initial paint.
    this.entitiesEl
      ?.querySelector<HTMLInputElement>('input[data-sb-search-entity="all"]')
      ?.removeAttribute("disabled");
    this.variantsEl
      ?.querySelector<HTMLInputElement>('input[data-sb-search-variant="all"]')
      ?.removeAttribute("disabled");
    this.sourcesEl
      ?.querySelector<HTMLInputElement>('input[data-sb-search-source="all"]')
      ?.removeAttribute("disabled");

    this.applyPrefsToEntityToggles();

    // Initialize the master "Everything" entity toggle from persisted prefs.
    if (this.entitiesEl) {
      const allInput = this.entitiesEl.querySelector<HTMLInputElement>(
        'input[data-sb-search-entity="all"]',
      );
      if (allInput) {
        const allOn =
          this.prefs.entities.titles &&
          this.prefs.entities.lyrics &&
          this.prefs.entities.translations &&
          this.prefs.entities.authors &&
          this.prefs.entities.ids &&
          this.prefs.entities.numbers &&
          this.prefs.entities.chapters &&
          this.prefs.entities.offered_to &&
          this.prefs.entities.extra_info &&
          this.prefs.entities.tags;
        allInput.checked = allOn;
      }
      this.updateEntityToggleVisibility();
    }

    this.inputEl?.addEventListener("input", () => {
      this.query = this.inputEl?.value ?? "";
      this.updateUrlFromState();
      this.collapseFiltersOnNextResults = true;
      this.triggerSearchSoon(250);
    });

    this.inputEl?.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.query = this.inputEl?.value ?? "";
        this.forceNextSearch = true;
        this.updateUrlFromState();
        this.collapseFiltersOnNextResults = true;
        this.triggerSearchSoon(0);

        // Dismiss the on-screen keyboard immediately so it does not cover results.
        if (isLikelyMobile()) {
          this.inputEl?.blur();
        }
      }
    });

    this.clearBtnEl?.addEventListener("click", () => {
      if (!this.inputEl) return;
      this.inputEl.value = "";
      this.query = "";
      this.forceNextSearch = false;
      this.updateUrlFromState();
      this.collapseFiltersOnNextResults = true;
      this.triggerSearchSoon(0);
      this.inputEl.focus();
    });

    // Toggle changes (entities + variants + books) via event delegation.
    this.rootEl.addEventListener("change", (ev: Event) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;

      const entity =
        target instanceof HTMLInputElement ? target.getAttribute("data-sb-search-entity") : null;
      if (entity && target instanceof HTMLInputElement) {
        const prev = { ...this.prefs.entities };
        const checked = !!target.checked;
        let syncEntityTogglesFromPrefs = true;

        if (entity === "all") {
          const allOn = !!target.checked;

          if (allOn) {
            // Turning "Everything" on enables all entities and collapses the children.
            this.prefs.entities.titles = true;
            this.prefs.entities.lyrics = true;
            this.prefs.entities.translations = true;
            this.prefs.entities.authors = true;
            this.prefs.entities.ids = true;
            this.prefs.entities.numbers = true;
            this.prefs.entities.chapters = true;
            this.prefs.entities.offered_to = true;
            this.prefs.entities.extra_info = true;
            this.prefs.entities.tags = true;
            this.applyPrefsToEntityToggles();
          } else {
            // Keep all entity prefs selected, but allow the master toggle itself
            // to stay off so the individual entity toggles are revealed.
            syncEntityTogglesFromPrefs = false;
          }
          // When turning "Everything" off, keep individual prefs as-is and simply reveal the individual toggles.
        } else {
          if (entity === "titles") this.prefs.entities.titles = checked;
          if (entity === "lyrics") this.prefs.entities.lyrics = checked;
          if (entity === "translations") this.prefs.entities.translations = checked;
          if (entity === "authors") this.prefs.entities.authors = checked;
          if (entity === "ids") this.prefs.entities.ids = checked;
          if (entity === "numbers") this.prefs.entities.numbers = checked;
          if (entity === "chapters") this.prefs.entities.chapters = checked;
          if (entity === "offered_to") this.prefs.entities.offered_to = checked;
          if (entity === "extra_info") this.prefs.entities.extra_info = checked;
          if (entity === "tags") this.prefs.entities.tags = checked;

          const allInput =
            this.entitiesEl?.querySelector<HTMLInputElement>(
              'input[data-sb-search-entity="all"]',
            ) ?? null;
          if (allInput) {
            const allOn =
              this.prefs.entities.titles &&
              this.prefs.entities.lyrics &&
              this.prefs.entities.translations &&
              this.prefs.entities.authors &&
              this.prefs.entities.ids &&
              this.prefs.entities.numbers &&
              this.prefs.entities.chapters &&
              this.prefs.entities.offered_to &&
              this.prefs.entities.extra_info &&
              this.prefs.entities.tags;

            allInput.checked = allOn;
          }
        }

        const changed = this.entitiesChanged(prev, this.prefs.entities);

        this.updateEntityToggleVisibility();
        if (syncEntityTogglesFromPrefs) {
          this.applyPrefsToEntityToggles();
        } else {
          this.updateFiltersSummaryText();
        }

        if (changed) {
          this.savePrefsScoped("entities");
          this.updateUrlFromState();
          this.collapseFiltersOnNextResults = false;
          this.triggerSearchSoon(0);
        }
        return;
      }

      const variant =
        target instanceof HTMLInputElement ? target.getAttribute("data-sb-search-variant") : null;
      if (variant && target instanceof HTMLInputElement) {
        const prev = { ...this.prefs.variants };

        if (variant === "all") {
          const allOn = !!target.checked;
          if (allOn) {
            for (const v of this.workerKnownVariants) {
              this.prefs.variants[v] = true;
            }
            this.applyPrefsToVariantToggles();
          }
          this.updateVariantToggleVisibility();
        } else {
          this.prefs.variants[variant] = !!target.checked;
          const allInput =
            this.variantsEl?.querySelector<HTMLInputElement>(
              'input[data-sb-search-variant="all"]',
            ) ?? null;
          const allOn =
            this.workerKnownVariants.length > 0 &&
            this.workerKnownVariants.every(v => this.prefs.variants[v] !== false);
          if (allInput) allInput.checked = allOn;
          this.applyPrefsToVariantToggles();
          this.updateVariantToggleVisibility();
        }

        const changed = this.mapGroupChanged(prev, this.prefs.variants, this.workerKnownVariants);

        if (changed) {
          this.savePrefsScoped("variants");
          this.updateUrlFromState();
          this.collapseFiltersOnNextResults = false;
          this.triggerSearchSoon(0);
        }
        return;
      }

      const source =
        target instanceof HTMLInputElement ? target.getAttribute("data-sb-search-source") : null;
      if (source && target instanceof HTMLInputElement) {
        const prev = { ...this.prefs.sources };

        if (source === "all") {
          const allOn = !!target.checked;
          if (allOn) {
            for (const s of this.workerKnownSources) {
              this.prefs.sources[s] = true;
            }
            this.applyPrefsToSourceToggles();
          }
          this.updateSourceToggleVisibility();
        } else {
          this.prefs.sources[source] = !!target.checked;
          const allInput =
            this.sourcesEl?.querySelector<HTMLInputElement>('input[data-sb-search-source="all"]') ??
            null;
          const allOn =
            this.workerKnownSources.length > 0 &&
            this.workerKnownSources.every(s => this.prefs.sources[s] !== false);
          if (allInput) allInput.checked = allOn;
          this.applyPrefsToSourceToggles();
          this.updateSourceToggleVisibility();
        }

        const changed = this.mapGroupChanged(prev, this.prefs.sources, this.workerKnownSources);

        if (changed) {
          this.savePrefsScoped("sources");
          this.updateUrlFromState();
          this.collapseFiltersOnNextResults = false;
          this.triggerSearchSoon(0);
        }
        return;
      }
    });

    this.rootEl.addEventListener("click", (ev: MouseEvent) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;

      const toggleBtn = target.closest<HTMLButtonElement>("[data-sb-search-filters-toggle]");
      if (toggleBtn) {
        ev.preventDefault();
        this.toggleFiltersCollapsed();
        return;
      }
    });
  }

  /** Shares the existing cover preview interaction between song and search results. */
  private readonly handleResultCoverClick = (event: MouseEvent): void => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey
    )
      return;
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest<HTMLElement>("[data-sb-search-cover-preview]");
    if (!button?.closest("[data-sb-search-root], [data-sb-song-root]")) return;
    event.preventDefault();
    this.openCoverPreview(button);
  };

  /**
   * Ensure scroll/viewport listeners try to relocate focus when input scrolls
   * out of view on mobile.
   */
  private ensureFocusRelocateListeners(): void {
    if (this.focusRelocateListenersAttached) return;
    this.focusRelocateListenersAttached = true;

    const pane = this.rootEl?.closest<HTMLElement>('[data-sb-pane="search"]');

    const schedule = (origin: string): void => {
      this.focusRelocateLastOrigin = origin;
      if (this.focusRelocateFrame != null) return;
      this.focusRelocateFrame = window.requestAnimationFrame(() => {
        this.focusRelocateFrame = null;
        const o = this.focusRelocateLastOrigin ?? "raf";
        this.focusRelocateLastOrigin = null;
        this.maybeRelocateFocusFromInput(o);
      });
    };

    pane?.addEventListener("scroll", () => schedule("pane-scroll"), { passive: true });
    window.addEventListener("scroll", () => schedule("window-scroll"), { passive: true });
    window.addEventListener("resize", () => schedule("window-resize"));
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", () => schedule("vv-resize"));
      window.visualViewport.addEventListener("scroll", () => schedule("vv-scroll"));
    }
  }

  private setLoadingState(
    state: "idle" | "indexing" | "searching" | "message",
    message?: string,
    i18nKey?: string,
    values?: Readonly<Record<string, TranslationInterpolationValue>>,
  ): void {
    this.loadingState = state;
    this.loadingMessage = message ?? null;
    this.loadingMessageI18nKey = i18nKey ?? null;
    this.loadingMessageValues = values;
    this.updateLoadingUi();
  }

  private setTranslatedLoadingState(
    state: "idle" | "indexing" | "searching" | "message",
    key: string,
    error?: unknown,
  ): void {
    this.setLoadingState(state, this.translatedStatusText(key, error), key);
  }

  private updateLoadingUi(): void {
    const el = this.loadingEl;
    const textEl = this.loadingTextEl;
    if (!el || !textEl) return;

    let text = "";
    let i18nKey: string | null = null;
    let values: Readonly<Record<string, TranslationInterpolationValue>> | undefined;

    const explicitMessage = this.loadingMessage;
    const explicitI18nKey = this.loadingMessageI18nKey;

    switch (this.loadingState) {
      case "indexing":
        if (explicitI18nKey || explicitMessage != null) {
          text = explicitMessage ?? "";
          i18nKey = explicitI18nKey;
          values = this.loadingMessageValues;
        } else {
          i18nKey = "search.loading";
          text = this.t.translateKey(i18nKey);
        }
        break;
      case "searching":
        if (explicitI18nKey || explicitMessage != null) {
          text = explicitMessage ?? "";
          i18nKey = explicitI18nKey;
          values = this.loadingMessageValues;
        } else {
          i18nKey = "search.searching";
          text = this.t.translateKey(i18nKey);
        }
        break;
      case "message":
      case "idle":
      default:
        text = explicitMessage ?? "";
        i18nKey = explicitI18nKey;
        values = this.loadingMessageValues;
        break;
    }

    if (i18nKey) {
      textEl.setAttribute("data-i18n", i18nKey);
      if (values) this.translationBinding.setValues(textEl, values);
      else this.translationBinding.clearValues(textEl);
      text = textEl.textContent ?? "";
      if (!text && explicitMessage) {
        text = explicitMessage;
        textEl.textContent = text;
      }
    } else {
      textEl.removeAttribute("data-i18n");
      this.translationBinding.clearValues(textEl);
      textEl.textContent = text;
    }

    const spinnerOn = this.loadingState === "indexing" || this.loadingState === "searching";
    const visible = spinnerOn || text.trim() !== "";

    if (visible) {
      el.classList.add("sb-search-status--visible");
    } else {
      el.classList.remove("sb-search-status--visible");
    }

    if (spinnerOn) {
      el.classList.remove("sb-search-status--no-spinner");
    } else {
      el.classList.add("sb-search-status--no-spinner");
    }
  }

  private toggleFiltersCollapsed(force?: boolean): void {
    if (!this.filtersWrapEl || !this.filtersToggleBtnEl) return;

    const next = force !== undefined ? force : !this.filtersCollapsed;
    this.filtersCollapsed = next;

    this.filtersWrapEl.dataset.open = next ? "0" : "1";
    this.filtersToggleBtnEl.setAttribute("aria-expanded", next ? "false" : "true");
  }

  private updateFiltersSummaryText(): void {
    if (!this.filtersSummaryEl) return;

    const entitiesTotal = Object.keys(this.prefs.entities).length;
    const entitiesSel = Object.values(this.prefs.entities).filter(Boolean).length;

    const variantsTotal = this.workerKnownVariants.length;
    const variantsSel = this.workerKnownVariants.filter(
      v => this.prefs.variants[v] !== false,
    ).length;

    const sourcesTotal = this.workerKnownSources.length;
    const sourcesSel = this.workerKnownSources.filter(s => this.prefs.sources[s] !== false).length;

    this.translationBinding.setValues(this.filtersSummaryEl, {
      books_selected: sourcesSel,
      books_total: sourcesTotal,
      variants_selected: variantsSel,
      variants_total: variantsTotal,
      entities_selected: entitiesSel,
      entities_total: entitiesTotal,
    });
  }

  private clearResultsAnimated(): void {
    if (!this.resultsEl) return;
    const el = this.resultsEl;

    el.removeAttribute("data-sb-has-results");

    if (prefersReducedMotion()) {
      el.replaceChildren();
      return;
    }

    window.setTimeout(() => {
      if (!el.hasAttribute("data-sb-has-results")) {
        el.innerHTML = "";
      }
    }, 220);
  }

  // ── Preferences ──────────────────────────────────────────────────────

  private loadPrefs(): SearchPrefs {
    try {
      const raw = localStorage.getItem(LS_KEY_PREFS);
      const parsed = raw ? (JSON.parse(raw) as Partial<SearchPrefs>) : null;

      const entities: EntityToggles = {
        titles: parsed?.entities?.titles ?? true,
        lyrics: parsed?.entities?.lyrics ?? true,
        translations: parsed?.entities?.translations ?? true,
        authors: (parsed as any)?.entities?.authors ?? true,
        ids: (parsed as any)?.entities?.ids ?? true,
        numbers: (parsed as any)?.entities?.numbers ?? true,
        chapters: (parsed as any)?.entities?.chapters ?? true,
        offered_to: (parsed as any)?.entities?.offered_to ?? true,
        extra_info: (parsed as any)?.entities?.extra_info ?? true,
        tags: (parsed as any)?.entities?.tags ?? true,
      };

      this.ensureEntitiesDefaults(entities);

      const variants =
        typeof parsed?.variants === "object" && parsed.variants
          ? (parsed.variants as Record<string, boolean>)
          : {};
      const sources =
        typeof parsed?.sources === "object" && parsed.sources
          ? (parsed.sources as Record<string, boolean>)
          : {};

      return { entities, variants, sources };
    } catch {
      return {
        entities: {
          titles: true,
          lyrics: true,
          translations: true,
          authors: true,
          ids: true,
          numbers: true,
          chapters: true,
          offered_to: true,
          extra_info: true,
          tags: true,
        },
        variants: {},
        sources: {},
      };
    }
  }

  private ensureEntitiesDefaults(entities: EntityToggles): void {
    const anyOn =
      entities.titles ||
      entities.lyrics ||
      entities.translations ||
      entities.authors ||
      entities.ids ||
      entities.numbers ||
      entities.chapters ||
      entities.offered_to ||
      entities.extra_info ||
      entities.tags;
    if (!anyOn) {
      entities.titles = true;
      entities.lyrics = true;
      entities.translations = true;
      entities.authors = true;
      entities.ids = true;
      entities.numbers = true;
      entities.chapters = true;
      entities.offered_to = true;
      entities.extra_info = true;
      entities.tags = true;
    }
  }

  private savePrefs(): void {
    try {
      localStorage.setItem(LS_KEY_PREFS, JSON.stringify(this.persistedPrefs));
    } catch {
      // ignore
    }
  }

  private mergePrefsWithKnownVariants(): void {
    let persistedChanged = false;
    for (const v of this.workerKnownVariants) {
      if (this.prefs.variants[v] === undefined) {
        this.prefs.variants[v] = true;
      }
      if (this.persistedPrefs.variants[v] === undefined) {
        this.persistedPrefs.variants[v] = true;
        persistedChanged = true;
      }
    }
    for (const s of this.workerKnownSources) {
      if (this.prefs.sources[s] === undefined) {
        this.prefs.sources[s] = true;
      }
      if (this.persistedPrefs.sources[s] === undefined) {
        this.persistedPrefs.sources[s] = true;
        persistedChanged = true;
      }
    }
    if (persistedChanged) this.savePrefs();
  }

  /** Return true if entity toggle set differs. */
  private entitiesChanged(prev: EntityToggles, next: EntityToggles): boolean {
    const keys = Object.keys(next) as (keyof EntityToggles)[];
    return keys.some(k => prev[k] !== next[k]);
  }

  /** Generic comparer for toggle maps (sources / variants). */
  private mapGroupChanged(
    prev: Record<string, boolean>,
    next: Record<string, boolean>,
    known: string[],
  ): boolean {
    return known.some(id => {
      const wasOn = prev[id] !== false;
      const isOn = next[id] !== false;
      return wasOn !== isOn;
    });
  }

  private applyPrefsToEntityToggles(): void {
    if (!this.entitiesEl) return;

    const set = (id: keyof EntityToggles, value: boolean): void => {
      const inp = this.entitiesEl!.querySelector<HTMLInputElement>(
        `input[data-sb-search-entity="${id}"]`,
      );
      if (inp) inp.checked = !!value;
    };

    set("titles", this.prefs.entities.titles);
    set("lyrics", this.prefs.entities.lyrics);
    set("translations", this.prefs.entities.translations);
    set("authors", this.prefs.entities.authors);
    set("ids", this.prefs.entities.ids);
    set("numbers", this.prefs.entities.numbers);
    set("chapters", this.prefs.entities.chapters);
    set("offered_to", this.prefs.entities.offered_to);
    set("extra_info", this.prefs.entities.extra_info);
    set("tags", this.prefs.entities.tags);

    const allInput = this.entitiesEl.querySelector<HTMLInputElement>(
      'input[data-sb-search-entity="all"]',
    );
    if (allInput) {
      allInput.checked = Object.values(this.prefs.entities).every(Boolean);
    }

    this.updateEntityToggleVisibility();

    this.updateFiltersSummaryText();

    // Update header count (selected/total) for entities
    const headerCount = this.entitiesEl
      .closest("[data-sb-search-filter-entities]")
      ?.querySelector<HTMLElement>("[data-sb-search-count-entities]");
    if (headerCount) {
      const total = Object.keys(this.prefs.entities).length;
      const selected = Object.values(this.prefs.entities).filter(Boolean).length;
      headerCount.textContent = total > 0 ? ` (${selected}/${total})` : "";
    }
  }

  // ── Searching ────────────────────────────────────────────────────────

  private triggerSearchSoon(delayMs: number): void {
    // Invalidate in-flight responses immediately, including while the new query is debounced.
    this.latestSearchRequestId = this.nextRequestId++;
    if (this.searchDebounceTimer != null) {
      window.clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    this.searchDebounceTimer = window.setTimeout(
      () => {
        this.searchDebounceTimer = null;
        this.runSearch();
      },
      Math.max(0, delayMs),
    );
  }

  private enabledVariantsList(): string[] {
    const out: string[] = [];
    for (const v of this.workerKnownVariants) {
      if (this.prefs.variants[v] !== false) out.push(v);
    }
    return out;
  }

  private enabledSourcesList(): string[] {
    const out: string[] = [];
    for (const s of this.workerKnownSources) {
      if (this.prefs.sources[s] !== false) out.push(s);
    }
    return out;
  }

  private runSearch(): void {
    if (!this.active || this.preservedSsrSignature === this.searchUrlSignature()) return;
    this.ensureDom();
    const requestId = this.nextRequestId++;
    this.latestSearchRequestId = requestId;

    const q = (this.query ?? "").trim();
    this.queryNorm = this.normalizeForUi(q);

    if (!this.indexReady) {
      // If index is still building, just show spinner.
      if (q) this.setLoadingState("indexing");
      void this.songIndex
        .init()
        .catch(e => this.showTranslatedError("search.unavailable-offline", e));
      return;
    }

    const forced = this.forceNextSearch;
    this.forceNextSearch = false;

    if (!q) {
      this.setLoadingState("idle");
      this.clearResultsAnimated();
      return;
    }

    if (!forced && q.length < 2 && !this.hasSearchParamsInUrl()) {
      this.setLoadingState("idle");
      this.clearResultsAnimated();
      return;
    }

    const entities = { ...this.prefs.entities };
    const anyEntity =
      entities.titles ||
      entities.lyrics ||
      entities.translations ||
      entities.authors ||
      entities.ids ||
      entities.numbers ||
      entities.chapters ||
      entities.offered_to ||
      entities.extra_info ||
      entities.tags;
    if (!anyEntity) {
      this.setTranslatedLoadingState("idle", "search.warning.no-entities");
      this.clearResultsAnimated();
      return;
    }

    const variants = this.enabledVariantsList();
    const sources = this.enabledSourcesList();
    if (this.workerKnownVariants.length > 0 && variants.length === 0) {
      this.setTranslatedLoadingState("idle", "search.warning.no-variants");
      this.clearResultsAnimated();
      return;
    }
    if (this.workerKnownSources.length > 0 && sources.length === 0) {
      this.setTranslatedLoadingState("idle", "search.warning.no-sources");
      this.clearResultsAnimated();
      return;
    }

    this.setLoadingState("searching");

    const opts: SearchOpts = {
      variants,
      sources,
      entities,
    };

    void this.songIndex
      .search(q, opts)
      .then(res => {
        if (requestId !== this.latestSearchRequestId || res.queryNorm !== this.queryNorm) return;
        this.renderResults(res.groups, { truncated: res.truncated });
        this.awaitingFirstResults = false;
      })
      .catch(e => {
        if (requestId !== this.latestSearchRequestId) return;
        this.showTranslatedError("search.unavailable-offline", e);
      });
  }

  /** Shows a translated persistent error above the normal search status area. */
  private showTranslatedError(key: string, error?: unknown): void {
    const el = this.errorEl;
    const textEl = this.errorTextEl;
    if (!el || !textEl) return;
    this.setLoadingState("idle");

    textEl.textContent = this.translatedStatusText(key, error);
    textEl.setAttribute("data-i18n", key);
    el.hidden = false;
    el.classList.add("sb-search-status--visible", "sb-search-status--no-spinner");
  }

  /** Hides the persistent search error message when index data becomes available. */
  private hideError(): void {
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.errorTextEl) {
      this.errorTextEl.textContent = "";
      this.errorTextEl.removeAttribute("data-i18n");
    }
  }

  private normalizeForUi(s: string): string {
    // Must match worker normalization.
    return String(s ?? "")
      .replace(/"/g, "")
      .normalize("NFD")
      .replace(/\p{M}+/gu, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  }

  // ── Rendering ────────────────────────────────────────────────────────

  private setResultsStatus(count: number, appearances: number, truncated: boolean): void {
    if (count === 0) {
      this.setLoadingState("idle", undefined, "search.results.none");
      return;
    }

    this.setLoadingState(
      "idle",
      undefined,
      `search.results.status.${truncated ? "truncated" : "full"}${count === 1 ? (appearances === 1 ? ".single" : ".one") : ""}`,
      { count, appearances },
    );
  }

  private applyPrefsToVariantToggles(): void {
    if (!this.variantsEl) return;

    // Update header count (selected/total) for variants
    const headerCount = this.variantsEl
      .closest("[data-sb-search-filter-variants]")
      ?.querySelector<HTMLElement>("[data-sb-search-count-variants]");
    if (headerCount) {
      const total = this.workerKnownVariants.length;
      const selected = this.workerKnownVariants.filter(
        v => this.prefs.variants[v] !== false,
      ).length;
      headerCount.textContent = total > 0 ? ` (${selected}/${total})` : "";
    }
    this.updateFiltersSummaryText();
    this.variantsEl
      .querySelectorAll<HTMLInputElement>(
        'input[data-sb-search-variant]:not([data-sb-search-variant="all"])',
      )
      .forEach(inp => {
        const v = inp.getAttribute("data-sb-search-variant") || "";
        const checked = this.prefs.variants[v] !== false;
        inp.checked = checked;
      });

    const allInput = this.variantsEl.querySelector<HTMLInputElement>(
      'input[data-sb-search-variant="all"]',
    );
    if (allInput) {
      allInput.checked =
        this.workerKnownVariants.length > 0 &&
        this.workerKnownVariants.every(v => this.prefs.variants[v] !== false);
    }
  }

  private updateSourceToggleVisibility(): void {
    const container = this.sourcesEl;
    if (!container) return;

    const allInput = container.querySelector<HTMLInputElement>(
      'input[data-sb-search-source="all"]',
    );
    const allOn = !!allInput?.checked;

    container
      .querySelectorAll<HTMLInputElement>(
        'input[data-sb-search-source]:not([data-sb-search-source="all"])',
      )
      .forEach(inp => {
        const label = inp.nextElementSibling as HTMLElement | null;
        const hide = allOn;

        inp.disabled = hide;
        if (hide) {
          inp.setAttribute("data-sb-hidden", "1");
          if (label) label.setAttribute("data-sb-hidden", "1");
        } else {
          inp.removeAttribute("data-sb-hidden");
          if (label) label.removeAttribute("data-sb-hidden");
        }
      });
  }

  private applyPrefsToSourceToggles(): void {
    const container = this.sourcesEl;
    if (!container) return;

    // Update header count (selected/total) for sources
    const headerCount = container
      .closest("[data-sb-search-filter-sources]")
      ?.querySelector<HTMLElement>("[data-sb-search-count-sources]");
    if (headerCount) {
      const total = this.workerKnownSources.length;
      const selected = this.workerKnownSources.filter(s => this.prefs.sources[s] !== false).length;
      headerCount.textContent = total > 0 ? ` (${selected}/${total})` : "";
    }

    this.updateFiltersSummaryText();

    container
      .querySelectorAll<HTMLInputElement>(
        'input[data-sb-search-source]:not([data-sb-search-source="all"])',
      )
      .forEach(inp => {
        const s = inp.getAttribute("data-sb-search-source") || "";
        const checked = this.prefs.sources[s] !== false;
        inp.checked = checked;
      });

    const allInput = container.querySelector<HTMLInputElement>(
      'input[data-sb-search-source="all"]',
    );
    if (allInput) {
      allInput.checked =
        this.workerKnownSources.length > 0 &&
        this.workerKnownSources.every(s => this.prefs.sources[s] !== false);
    }
  }

  private openCoverPreview(sourceEl: HTMLElement): void {
    this.closeCoverPreview(false);

    const sourceRect = sourceEl.getBoundingClientRect();
    if (sourceRect.width <= 0 || sourceRect.height <= 0) return;

    const clone = sourceEl.cloneNode(true) as HTMLElement;
    clone.removeAttribute("id");
    clone.removeAttribute("data-sb-search-cover-preview");
    clone.removeAttribute("aria-label");
    clone.classList.add("sb-search-cover-preview-card");
    clone.insertAdjacentHTML(
      "beforeend",
      '<i class="sbi-x sb-search-cover-preview-close" aria-hidden="true"></i>',
    );

    const img = sourceEl.querySelector<HTMLImageElement>("img.sb-search-meta-cover");
    const aspect =
      img?.naturalWidth && img?.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : sourceRect.width / Math.max(1, sourceRect.height);

    const remPx =
      Number.parseFloat(getComputedStyle(document.documentElement).fontSize || "16") || 16;
    const maxWidth = Math.min(25 * remPx, window.innerWidth - 32);
    const maxHeight = window.innerHeight - 32;
    let finalWidth = Math.max(sourceRect.width, maxWidth);
    let finalHeight = finalWidth / Math.max(0.01, aspect);

    if (finalHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = finalHeight * aspect;
    }

    const finalLeft = (window.innerWidth - finalWidth) / 2;
    const finalTop = (window.innerHeight - finalHeight) / 2;
    const scaleX = sourceRect.width / finalWidth;
    const scaleY = sourceRect.height / finalHeight;
    const translateX = sourceRect.left - finalLeft;
    const translateY = sourceRect.top - finalTop;

    const overlay = document.createElement("div");
    overlay.className = "sb-search-cover-preview-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.appendChild(clone);

    clone.style.left = `${finalLeft}px`;
    clone.style.top = `${finalTop}px`;
    clone.style.width = `${finalWidth}px`;
    clone.style.height = `${finalHeight}px`;
    clone.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;

    document.body.appendChild(overlay);
    this.coverPreviewEl = overlay;
    this.coverPreviewSource = sourceEl;

    overlay.addEventListener("click", () => this.closeCoverPreview(true), { once: true });
    document.addEventListener("keydown", this.handleCoverPreviewKeydown, { capture: true });

    if (prefersReducedMotion()) {
      overlay.dataset.open = "1";
      clone.style.transform = "translate(0, 0) scale(1, 1)";
      return;
    }

    // Force the browser to commit the small/source-sized initial transform before
    // switching to the expanded state. Without this, some engines coalesce the
    // style changes and the opening transition is skipped.
    void clone.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      overlay.dataset.open = "1";
      clone.style.transform = "translate(0, 0) scale(1, 1)";
    });
  }

  private closeCoverPreview(animated: boolean): void {
    const overlay = this.coverPreviewEl;
    if (!overlay) return;

    const card = overlay.querySelector<HTMLElement>(".sb-search-cover-preview-card");
    const source = this.coverPreviewSource;
    this.coverPreviewEl = null;
    this.coverPreviewSource = null;
    document.removeEventListener("keydown", this.handleCoverPreviewKeydown, {
      capture: true,
    } as AddEventListenerOptions);

    const remove = () => overlay.remove();
    if (!animated || prefersReducedMotion() || !card || !source?.isConnected) {
      remove();
      return;
    }

    const sourceRect = source.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const scaleX = sourceRect.width / Math.max(1, cardRect.width);
    const scaleY = sourceRect.height / Math.max(1, cardRect.height);
    const translateX = sourceRect.left - cardRect.left;
    const translateY = sourceRect.top - cardRect.top;

    overlay.dataset.open = "0";
    card.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;
    window.setTimeout(remove, 220);
  }

  private handleCoverPreviewKeydown = (ev: KeyboardEvent): void => {
    if (ev.key !== "Escape") return;
    ev.preventDefault();
    this.closeCoverPreview(true);
  };

  private renderResults(groups: GroupedSongResult[], opts: { truncated: boolean }): void {
    if (!this.resultsEl) return;

    const restoreScroll = this.captureSearchScrollState();

    const hasResults = groups.length > 0;
    if (hasResults && this.collapseFiltersOnNextResults) {
      this.toggleFiltersCollapsed(true);
    }

    if (!hasResults) {
      this.resultsEl.innerHTML = "";
      this.resultsEl.removeAttribute("data-sb-has-results");
      this.setResultsStatus(0, 0, false);
      restoreScroll();
      return;
    }

    const destinationHashParam =
      String((this.state.site as any).pdfDestinationHashParam ?? "song").trim() || "song";

    const list = document.createElement("div");
    list.className = "sb-search-hitlist";
    for (const group of groups) {
      list.append(
        renderGroupedSongResult(group, {
          translator: this.t,
          books: this.state.books,
          destinationHashParam,
          defaultLanguage: this.state.site.defaultLanguage,
          queryNorm: this.queryNorm,
          showCovers: this.workerKnownSources.length > 1,
          onDetailsRendered: body => this.translationBinding.update(body),
        }),
      );
    }
    this.resultsEl.replaceChildren(list);
    this.resultsEl.setAttribute("data-sb-has-results", "1");

    this.setResultsStatus(
      groups.length,
      groups.reduce((count, group) => count + group.appearances.length, 0),
      opts.truncated,
    );

    // Ensure i18n is applied to meta labels + book titles.
    this.translationBinding.update(this.resultsEl);

    // If the focused mobile input moved off-screen, dismiss the keyboard.
    if (hasResults) {
      this.maybeRelocateFocusFromInput("render-results");
    }

    restoreScroll();
  }

  private captureSearchScrollState(): () => void {
    const pane = this.rootEl?.closest<HTMLElement>('[data-sb-pane="search"]') ?? null;
    const paneScrollTop = pane?.scrollTop ?? null;
    const windowScrollX = window.scrollX;
    const windowScrollY = window.scrollY;

    return () => {
      window.requestAnimationFrame(() => {
        if (pane && pane.isConnected && paneScrollTop != null) pane.scrollTop = paneScrollTop;
        window.scrollTo(windowScrollX, windowScrollY);
      });
    };
  }

  /**
   * If the search input is focused and off-screen on mobile, blur it to hide the keyboard.
   */
  private maybeRelocateFocusFromInput(_origin: string): void {
    if (this.returningToSearchInput) {
      const pane = this.rootEl?.closest<HTMLElement>('[data-sb-pane="search"]');
      if (pane && pane.scrollTop > 1) return;
      this.returningToSearchInput = false;
    }
    const active = document.activeElement;
    const mobile = isLikelyMobile();
    const offscreen = this.inputEl ? isElementOutOfViewport(this.inputEl) : false;

    if (!this.inputEl || active !== this.inputEl) return;
    if (!mobile) return;
    if (!offscreen) return;

    this.inputEl.blur();
  }

  private updateEntityToggleVisibility(): void {
    if (!this.entitiesEl) return;

    const allInput = this.entitiesEl.querySelector<HTMLInputElement>(
      'input[data-sb-search-entity="all"]',
    );
    const allOn = !!allInput?.checked;

    this.entitiesEl
      .querySelectorAll<HTMLInputElement>(
        'input[data-sb-search-entity]:not([data-sb-search-entity="all"])',
      )
      .forEach(inp => {
        const label = inp.nextElementSibling as HTMLElement | null;
        const hide = allOn;

        inp.disabled = hide;
        if (hide) {
          inp.setAttribute("data-sb-hidden", "1");
          if (label) label.setAttribute("data-sb-hidden", "1");
        } else {
          inp.removeAttribute("data-sb-hidden");
          if (label) label.removeAttribute("data-sb-hidden");
        }
      });
  }

  private updateVariantToggleVisibility(): void {
    if (!this.variantsEl) return;

    const allInput = this.variantsEl.querySelector<HTMLInputElement>(
      'input[data-sb-search-variant="all"]',
    );
    const allOn = !!allInput?.checked;

    this.variantsEl
      .querySelectorAll<HTMLInputElement>(
        'input[data-sb-search-variant]:not([data-sb-search-variant="all"])',
      )
      .forEach(inp => {
        const label = inp.nextElementSibling as HTMLElement | null;
        const hide = allOn;

        inp.disabled = hide;
        if (hide) {
          inp.setAttribute("data-sb-hidden", "1");
          if (label) label.setAttribute("data-sb-hidden", "1");
        } else {
          inp.removeAttribute("data-sb-hidden");
          if (label) label.removeAttribute("data-sb-hidden");
        }
      });
  }
}
